import { Button, Typography, CircularProgress, Backdrop, IconButton, Box } from "@mui/material";
import React, { useState, useCallback, memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBondLadderRungFlowStore } from "../../Store/bondLadderRungFlowStore";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { useBondLadderStore } from "../../Store/bondLadderStore";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import { useListRfqStore } from "../../Store/listRfqStore";
import { buildPrefillRows, buildViewSharedFields } from "../OrderEntry/listRfqPrefill";
import { getBondCharacteristicById, getBondLadderById, cancelOrder, cancelListOrder, fetchListRfqItems, fetchRfqStrategyParams, getPendingCharacteristicsByLadderId } from "../../http/api";
import useSnackbar from "../../Utils/useSnackbar";
import { ENHANCED_RFQ_PROTOCOLS } from "./RfqComponents/RfqConstants";
import { handleViewInventory, openInventoryForCharacteristic } from "../../Utils/viewInventoryHandler";
import { processBondData } from "./utils";
import { 
  COMMON_STYLES, 
  parseCharacteristics, 
  preprocessFilters,
  buildOrderColumns,
  buildBondCharColumns,
  buildBondLadderColumns
} from "./columnConfigUtils";
import { tokens, withAlpha } from '../../Common/designTokens'; // added
import FilterChips from "../InventoryComponents/FilterChips";
import EditBondLadder from '../BondLadderComponents/EditBondLadder';
import EditBondCharacteristic from '../BondLadderComponents/EditBondCharacteristic'; // new import
import LadderPendingCharacteristicsModal from '../BondLadderComponents/LadderPendingCharacteristicsModal';
import RfqCancellationConfirmPopup from "./RfqComponents/RfqCancellationConfirmPopup";
import { formatListId } from "../../Utils/listOrderId";
import { isRfqCancelBlocked } from "../../Utils/listRfqStatus";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OrderEntry from "../OrderEntry";
import { PROTOCOL_TYPES } from "../../Constants/AppConstants";
import { stagingOrderFields } from "../../Constants/stagingBlotter";

// Global state and constants
let setGlobalLoading = null;
const BUTTON_PADDING = "4px 4px"; // changed: reduced horizontal padding
const BUTTON_MIN_WIDTH = "auto";

// Memoized components for better performance
const LoadingOverlay = memo(() => {
  const [loading, setLoading] = useState(false);
  setGlobalLoading = setLoading;
  
  return (
    <Backdrop
      sx={{
        color: tokens.colors.white,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: withAlpha('#000000', 0.7)
      }}
      open={loading}
    >
      <CircularProgress sx={{ color: tokens.colors.white }} size={60} />
      <Typography variant="h6" sx={{ color: tokens.colors.white }}>Loading data...</Typography>
    </Backdrop>
  );
});

const ActionButton = memo(({ onClick, loading, label, title }) => (
  <Button 
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }} 
    size="small" 
    disabled={loading}
    sx={{
      ...COMMON_STYLES.button,
      padding: BUTTON_PADDING,
    }}
  >
    <Typography 
      variant="body2" 
      sx={COMMON_STYLES.typography}
      title={title}
    >
      {loading ? <CircularProgress size={16} /> : label}
    </Typography>
  </Button>
));
const ActionButtonViewInv = memo(({ onClick, loading, label, title }) => (
  <Button 
    onClick={onClick} 
    disabled={loading}
    size="small"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      color: tokens.palette.primary,
      '&:hover': {
        backgroundColor: withAlpha(tokens.palette.primary, 0.10),
      },
      border: `1px solid ${tokens.palette.primary}`,
      minWidth: BUTTON_MIN_WIDTH,
      width: 'auto',
      height: 'auto',
      minHeight: 'auto',
      padding: BUTTON_PADDING,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1,
      textTransform: 'none',
      marginRight: '10px',
      borderRadius: '8px',
      boxShadow: 'none',
    }}
    title={title}
  >
    {loading ? <CircularProgress size={16} /> : label}
  </Button>
));

const ActionButtonEditRfq = memo(({ disabled, onClick, loading, label, title }) => (
  <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      title={title}//complete this
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: "transparent",
        color: tokens.colors.fontColour90,
        padding: BUTTON_PADDING,
        minWidth: BUTTON_MIN_WIDTH,
        width: 'auto',
        height: 'auto',
        minHeight: 'auto',
        borderRadius: '8px',
        border: `1px solid ${tokens.colors.bodyBorderColor}`,
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1,
        textTransform: 'none',
        marginRight: '10px',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: tokens.colors.defaultGridHoverColor,
        },
      }}
  >
    {loading ? <CircularProgress size={16} sx={{ color: tokens.colors.white }} /> : label}
  </Button>
));
const ActionButtonEdit = memo(({ onClick, loading, label, title }) => (
  <Button
    onClick={onClick}
    disabled={loading}
    // variant="contained"
    size="small"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: "transparent",
      color: tokens.colors.fontColour90,
      padding: BUTTON_PADDING,
      minWidth: BUTTON_MIN_WIDTH,
      width: 'auto',
      height: 'auto',
      minHeight: 'auto',
      borderRadius: '8px',
      border: `1px solid ${tokens.colors.bodyBorderColor}`,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1,
      textTransform: 'none',
      marginRight: '10px',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: tokens.colors.defaultGridHoverColor,
      },
      '&.Mui-disabled': {
        backgroundColor: tokens.colors.disabledBackground,
        color: tokens.colors.disabledFontColour,
        border: 'none',
      },
    }}
    title={title}
  >
    {loading ? <CircularProgress size={16} sx={{ color: tokens.colors.white }} /> : label}
  </Button>
));
const ActionButtonResubmit = memo(({ onClick, loading, label, title }) => (
  <Button 
    onClick={onClick} 
    disabled={loading}
    color="inherit"
    variant="none"
    size="small"
    sx={{
      backgroundColor: 'transparent',
      color: tokens.colors.fontColour,
      '&:hover': {
        backgroundColor: withAlpha(tokens.palette.primary, 0.10),
        color: tokens.colors.fontColour,
      },
      border: `1px solid ${tokens.palette.primary}`,
      minWidth: BUTTON_MIN_WIDTH,
      padding: BUTTON_PADDING,
      fontSize: '0.875rem !important',
      lineHeight: 1.2,
    }}
  >
    <Typography 
      variant="body2" 
      sx={{
        ...COMMON_STYLES.typography,
        textDecoration: 'none',
        color: tokens.palette.primary,
        // fontSize: '0.75rem',
      }}
      title={title}
    >
      {loading ? <CircularProgress size={16} /> : label}
    </Typography>
  </Button>
));

// NEW: account positions actions (edit/delete placeholders)
const AccountPositionActionsCell = memo(({ rowData, onEdit, onDelete }) => {
  const handleClick = (e, cb) => {
    e?.stopPropagation();
    if (cb) cb(rowData);
  };
  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
      <IconButton
        size="small"
        onClick={(e) => handleClick(e, onEdit)}
        title="Edit"
        sx={{
          color: tokens.colors.fontColour,
          "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor }
        }}
      >
        <EditOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => handleClick(e, onDelete)}
        title="Delete"
        sx={{
          color: tokens.colors.fontColour,
          "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor }
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

// ViewInventoryColumn component for direct inventory navigation
const ViewInventoryColumn = memo(({ rowData, type }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCharModalOpen, setEditCharModalOpen] = useState(false); // new
  const [orderEntryOpen, setOrderEntryOpen] = useState(false); // Add state for OrderEntry popup
  const [pendingCharsLoading, setPendingCharsLoading] = useState(false);
  const [pendingChars, setPendingChars] = useState([]);
  const [pendingCharsModalOpen, setPendingCharsModalOpen] = useState(false);
  const setCurrentCharacteristicID = useBondCharacteristicStore(state => state.setCurrentCharacteristicID);
  const setActualOrderData = useActualOrderStore(state => state.setActualOrderData);
  const resetBondCharacteristicData = useBondCharacteristicStore(state => state.resetBondCharacteristicData);
  const setBondCharacteristicData = useBondCharacteristicStore(state => state.setBondCharacteristicData);
  const setLadderData = useBondLadderRungFlowStore(state => state.setLadderData);
  const setCharacteristicLadderContext = useBondCharacteristicStore(state => state.setCharacteristicLadderContext);

  const handleViewInventoryClick = useCallback(async () => {
    try {
      setLoading(true);
      if (setGlobalLoading) setGlobalLoading(true);
      
      const flattenedOrder = processBondData(rowData);
      
      if (type === "characteristic") {
        resetBondCharacteristicData();
        setBondCharacteristicData(flattenedOrder);
        
        handleViewInventory(
          1,
          flattenedOrder,
          setCurrentCharacteristicID,
          setActualOrderData,
          navigate,
          (error) => {
            console.error("Error navigating to inventory:", error);
            alert(error);
          },
          setLoading
        );
      } else if (type === "ladder") {
        setLadderData(flattenedOrder);
        
        handleViewInventory(
          2,
          flattenedOrder,
          setCurrentCharacteristicID,
          setActualOrderData,
          navigate,
          (error) => {
            console.error("Error navigating to ladder inventory:", error);
            alert(error);
          },
          setLoading
        );
      }
    } catch (error) {
      console.error("Error handling inventory view:", error);
      alert("Failed to navigate to inventory view");
    } finally {
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [rowData, type, navigate, setCurrentCharacteristicID, setActualOrderData, resetBondCharacteristicData, setBondCharacteristicData, setLadderData]);

  // Ladder roll-over: open inventory for one of the ladder's pending characteristics.
  // Reuses the exact flow the Bond Characteristic tab runs.
  const openInventoryForRow = useCallback((pendingRow) => {
    try {
      if (setGlobalLoading) setGlobalLoading(true);
      openInventoryForCharacteristic(pendingRow.characteristic, {
        navigate,
        showError: (error) => {
          console.error("Error navigating to inventory:", error);
          alert(error);
        },
        setLoading: setPendingCharsLoading,
        // Every rung of a ladder is placed on the ladder's account
        fallbackAccount: rowData.account,
      });
    } finally {
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [navigate, rowData.account]);

  const handleViewCharacteristicsClick = useCallback(async () => {
    const ladderId = rowData.bondLadderId;
    if (!ladderId) return;

    try {
      setPendingCharsLoading(true);
      // full-screen overlay: the rung picker can take a moment to appear
      if (setGlobalLoading) setGlobalLoading(true);
      const token = localStorage.getItem("token");
      const rows = await getPendingCharacteristicsByLadderId(token, ladderId);
      const pending = Array.isArray(rows) ? rows.filter((row) => row?.characteristic) : [];

      if (pending.length === 0) {
        alert("No pending characteristics for this ladder.");
        return;
      }
      // One rung waiting: skip the picker and go straight to inventory
      if (pending.length === 1) {
        openInventoryForRow(pending[0]);
        return;
      }
      setPendingChars(pending);
      setPendingCharsModalOpen(true);
    } catch (error) {
      console.error("Error fetching ladder characteristics:", error);
      alert("Failed to load characteristics for this ladder");
    } finally {
      setPendingCharsLoading(false);
      // must clear before the picker renders — the overlay sits above the modal
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [rowData.bondLadderId, openInventoryForRow]);

  const handleEditClick = useCallback(() => {
    if (type === 'ladder') setEditModalOpen(true);
    if (type === 'characteristic') setEditCharModalOpen(true); // new
  }, [type]);

  const handleEditSubmit = useCallback((updatedData) => {
    // TODO: Implement API call to update the bond ladder
    console.log('Updated ladder data:', updatedData);
    // You can add API call here to save the updated data
    setEditModalOpen(false);
  }, []);

  const handleCharEditSubmit = useCallback((updatedData) => {
    console.log('Updated characteristic data:', updatedData);
    setEditCharModalOpen(false);
  }, []);

  // NEW: handler for Add Order button
  const handleAddOrderClick = useCallback(() => {
    console.log('Add Order clicked for characteristic:', rowData);
    const flattenedOrder = processBondData(rowData);
    const characteristicId =
      rowData.characteristicId || rowData.characteristicMstId || rowData.id;

    resetBondCharacteristicData();
    setBondCharacteristicData(flattenedOrder);
    setCurrentCharacteristicID(characteristicId);
    setCharacteristicLadderContext(rowData);
    setActualOrderData({
      characteristicId,
      // The order belongs on the account the characteristic was placed on. Written
      // unconditionally so a previously viewed order's account cannot linger; when the
      // characteristic has none, ActualOrderTab falls back to the user's first account.
      account: rowData.account ?? "",
      // The characteristic dictates which way the order goes; ActualOrderTab locks the field.
      // Upper-cased to match the Side dropdown's BUY / SELL options — a locked field whose
      // value is not one of the options would render blank with no way to correct it.
      side: String(rowData.side ?? "").trim().toUpperCase(),
      bondLadderId: rowData.bondLadderId ?? null,
      bondLadderRungId: rowData.bondLadderRungId ?? null,
      // Ladder-linked orders always route to the trading desk over CLOB; venue +
      // protocol are then locked in ActualOrderTab
      ...(rowData.bondLadderId
      // Explicitly no dealer: this form's Dealer field is editable, so the
      // LEGACY_STAGING_VENUE placeholder stagingOrderFields() falls back to would read as a
      // real choice. Start empty; it fills in from the bond once a CUSIP verifies (and only
      // if that dealer is one the protocol offers). Routing is unaffected either way - the
      // staging flag decides it, and venue is a required field so an empty one cannot be
      // submitted.
        ? { ...stagingOrderFields(""), protocol: PROTOCOL_TYPES.CLOB }
        : {}),
    });
    setOrderEntryOpen(true);
  }, [rowData, setActualOrderData, resetBondCharacteristicData, setBondCharacteristicData, setCurrentCharacteristicID, setCharacteristicLadderContext]);


  const status = (rowData.status || '').toString().toUpperCase();
  if (status === 'NOT AVAILABLE' || status === 'ORDER PLACED') {
    return <>---</>;
  }

  // NEW: Only allow view/edit for the user who created the entry
  const currentUserId = localStorage.getItem("userId");
  const canAct = String(rowData.entryBy ?? "") === String(currentUserId ?? "");

  // A ladder awaiting a replacement bond has exactly one action: pick the rung whose
  // characteristic to shop for. Edit and View Inventory are suppressed for this status.
  const isReplacementRequired =
    type === "ladder" &&
    (status === "REPLACEMENT_REQUIRED" || status === "REPLACEMENT REQUIRED");
  const hasPendingCharacteristics = Number(rowData.pendingCharacteristicCount) > 0;

  // Nothing to offer: the one allowed action is unavailable, and the others are hidden
  if (isReplacementRequired && !(canAct && hasPendingCharacteristics)) {
    return <>---</>;
  }

  // Center align the button(s)
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
        {/* Hide Edit and View Inventory when the current user is not the entry creator */}
        {canAct && type === "characteristic" && (
          <ActionButtonEdit
            onClick={handleEditClick}
            loading={false}
            label="Edit"
            title="Edit Characteristic"
          />
        )}
        {canAct && type === "ladder" && !isReplacementRequired && (
          <ActionButtonEdit
            onClick={handleEditClick}
            loading={false}
            label="Edit"
            title="Edit Ladder"
          />
        )}
        {!isReplacementRequired &&
          (canAct ? (
            <ActionButtonViewInv onClick={handleViewInventoryClick} loading={loading} label="View Inventory" />
          ) : (
            // when hidden, keep placeholder consistent
            <>---</>
          ))}
        {canAct && type === "characteristic" && (
          <ActionButtonEdit
            onClick={handleAddOrderClick}
            loading={false}
            label="Add Order"
            title="Add Order for Characteristic"
          />
        )}
        {/* Ladder roll-over: only when the ladder needs a replacement and a rung is waiting */}
        {canAct && isReplacementRequired && hasPendingCharacteristics && (
          <ActionButtonViewInv
            onClick={handleViewCharacteristicsClick}
            loading={pendingCharsLoading}
            label="View Characteristics"
            title="View characteristics awaiting a bond on this ladder"
          />
        )}
      </div>
      {type === "ladder" && canAct && (
        <EditBondLadder
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          ladderData={rowData}
          onSubmit={handleEditSubmit}
        />
      )}
      {/* Rung picker, shown only when the ladder has more than one pending characteristic */}
      {type === "ladder" && canAct && pendingCharsModalOpen && (
        <LadderPendingCharacteristicsModal
          open={pendingCharsModalOpen}
          onClose={() => setPendingCharsModalOpen(false)}
          ladderName={rowData.nameOfTheLadder}
          rows={pendingChars}
          onSelect={(row) => {
            setPendingCharsModalOpen(false);
            openInventoryForRow(row);
          }}
        />
      )}
      {type === "characteristic" && canAct && (
        <EditBondCharacteristic
          open={editCharModalOpen}
          onClose={() => setEditCharModalOpen(false)}
          characteristicData={rowData}
          onSubmit={handleCharEditSubmit}
        />
      )}
      {/* Add OrderEntry popup */}
      {type === "characteristic" && canAct && orderEntryOpen && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="orderViaCharacteristic"
          initialActiveTab={0}
          initialData={{ 
            characteristicId: rowData.characteristicId || rowData.characteristicMstId || rowData.id 
          }}
        />
      )}
    </>
  );
});

// Enhanced RFQ Actions cell component
const EnhancedRfqActionsCell = memo(({ rowData }) => {
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const orderId = rowData?.orderId;
  const accountId = rowData?.account ?? rowData?.account?.accountId ?? rowData?.account?.id ?? null;
  const protocolName = rowData?.protocol ? String(rowData.protocol) : "";
  const rfqStatus = (rowData?.rfqStatus || "").toUpperCase();
  const isEnhanced = useMemo(
    () =>
      protocolName &&
      ENHANCED_RFQ_PROTOCOLS.some(
        (proto) => proto?.toLowerCase() === protocolName.toLowerCase()
      ),
    [protocolName]
  );

  const missingRfqStatus = !rowData?.rfqStatus;
  // NEW: consider MarketAxess protocols as valid for showing cancel button
  const isMarketAxess = protocolName && protocolName.toLowerCase().includes("marketaxess");
  // Show placeholder only when rfqStatus is missing OR neither an enhanced protocol nor MarketAxess OR missing orderId
  const showPlaceholder = missingRfqStatus || (!isEnhanced && !isMarketAxess) || !orderId;
  const isRejected = rfqStatus === "REJECTED";
  const isExpired = rfqStatus === "EXPIRED";
  const isBlockedStatus = isRfqCancelBlocked(rfqStatus);

  // NEW: determine whether current user is the entry creator
  const currentUserId = localStorage.getItem("userId");
  const canAct = String(rowData.entryBy ?? "") === String(currentUserId ?? "");

  // Either venue can leave live siblings after this particular bond finishes.
  const isListRfq = !!(rowData?.tradingSessionSubId || rowData?.mktxListId);
  const cancelDisabled = loading || isBlockedStatus || !accountId || !canAct;
  const resubmitDisabled = !canAct || !accountId;

  // Trumid identifies an item with 625; MarketAxess lists share 66 instead.
  // The bonds of this list, for the confirmation dialog. Fetched when the dialog opens rather
  // than read off rowData: the blotter is paged, so a list's siblings may not even be on
  // screen. GET /order/list/{id}/items is the only thing that can see the whole list.
  const [listItems, setListItems] = useState([]);
  const [listItemsLoading, setListItemsLoading] = useState(false);

  const sharedOwnerTooltip = "Only the user who placed this RFQ can perform this action";
  const cancelTooltip = !canAct
    ? sharedOwnerTooltip
    : isBlockedStatus
    ? "Action unavailable for current RFQ status"
    : undefined;
  const resubmitTooltip = !canAct ? sharedOwnerTooltip : undefined;

  // --- ADDED: edit button state, gating and handler (same logic as cancel)
  const [editLoading, setEditLoading] = useState(false);
  const editDisabled = cancelDisabled; // same gating as cancel
  const editTooltip = cancelTooltip;
  const handleEditClick = useCallback((e) => {
    e && e.stopPropagation();
    if (editDisabled) return;
    // No-op for now; emit custom event for later wiring
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("order:edit", { detail: rowData }));
      }
    } catch (err) {
      // fail silently for now
      console.error("order:edit dispatch failed", err);
    }
  }, [editDisabled, rowData]);
  // --- END ADDED

  const handleResubmitClick = useCallback((e) => {
    e?.stopPropagation();
    if (resubmitDisabled || typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("order:resubmit", { detail: rowData }));
  }, [resubmitDisabled, rowData]);

  // The single entry point for BOTH cancels. There is no separate Cancel List button on the
  // row any more: a list row opens this same dialog with the list's bonds drawn in it and a
  // third button beside Yes, so the trader can see exactly what a list cancel would pull
  // before choosing between the two. A row button could never show that.
  //
  // Gated on cancelDisabled - THIS bond's own status - which is now exactly right, because
  // Yes cancels exactly this bond. (The backend picks the message off the same tag 625:
  // 298=3 "Cancel Line Item" for a list row, 298=4 for a single-bond RFQ.)
  //
  // Opens the dialog straight away and loads the bonds behind it, rather than making the
  // trader wait on a fetch before anything appears. The dialog is fully usable while the
  // table is still loading - fetchListRfqItems returns [] on any failure, so a slow or broken
  // lookup degrades to the dialog without the table instead of blocking the cancel.
  const handleCancelClick = useCallback(async (e) => {
    e && e.stopPropagation();
    if (cancelDisabled) return;
    setConfirmOpen(true);
    if (!isListRfq) return;

    // Read here, not from component scope - every other handler in this cell does the same.
    const token = localStorage.getItem("token");
    if (!token || !orderId) return;

    setListItems([]);  // never show the previous open's bonds while the refetch is in flight
    setListItemsLoading(true);
    try {
      setListItems(await fetchListRfqItems(token, orderId));
    } finally {
      setListItemsLoading(false);
    }
  }, [cancelDisabled, isListRfq, orderId]);

  // UPDATED: normalize success detection and show only green on success
  const handleConfirmCancel = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Authentication required");
      return;
    }
    if (!orderId || !accountId) {
      showError("Missing order context");
      return;
    }
    try {
      setLoading(true);
      const response = await cancelOrder(token, orderId, accountId);

      // Normalize success flag across possible response shapes
      const success =
        response === true ||
        response?.success === true ||
        String(response?.success).toLowerCase() === "true" ||
        response?.data?.success === true ||
        String(response?.data?.success).toLowerCase() === "true" ||
        String(response?.status).toUpperCase() === "SUCCESS" ||
        String(response?.data?.status).toUpperCase() === "SUCCESS";

      if (success) {
        const msg =
          response?.message ||
          response?.data?.message ||
          "Order cancelled successfully";
        showSuccess(msg); // green only
      } else {
        const errMsg =
          response?.message ||
          response?.data?.message ||
          "Failed to cancel order";
        showError(errMsg);
      }
    } catch (error) {
      showError(error?.message || "Failed to cancel order");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  }, [accountId, orderId, showError, showSuccess]);

  /**
   * Cancels the WHOLE list this bond belongs to. Reached from the Cancel List button inside
   * the confirmation dialog, not from the blotter row.
   *
   * Mirrors handleConfirmCancel above statement for statement - same token check, same
   * context check, same tolerant success detection across response shapes, same snackbar
   * handling - so the two cancels behave identically from the trader's point of view.
   *
   * The only difference is the call: cancelListOrder hits /order/list/cancel/{id}, which
   * takes ANY order id belonging to the list and pulls every bond in it.
   */
  const handleConfirmCancelList = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Authentication required");
      return;
    }
    if (!orderId || !accountId) {
      showError("Missing order context");
      return;
    }
    try {
      // No loading flag of its own: the dialog owns the spinner on its Cancel List button and
      // keeps itself open until this resolves. handleConfirmCancel is the same shape.
      const response = await cancelListOrder(token, orderId, accountId);

      const success =
        response === true ||
        response?.success === true ||
        String(response?.success).toLowerCase() === "true" ||
        response?.data?.success === true ||
        String(response?.data?.success).toLowerCase() === "true" ||
        String(response?.status).toUpperCase() === "SUCCESS" ||
        String(response?.data?.status).toUpperCase() === "SUCCESS";

      if (success) {
        showSuccess(
          response?.message ||
            response?.data?.message ||
            "List RFQ cancel request sent successfully"
        );
      } else {
        showError(
          response?.message || response?.data?.message || "Failed to cancel list RFQ"
        );
      }
    } catch (error) {
      showError(error?.message || "Failed to cancel list RFQ");
    } finally {
      setConfirmOpen(false);
    }
  }, [accountId, orderId, showError, showSuccess]);

  // NEW: return placeholder after all hooks to satisfy hooks rules
  if (showPlaceholder) return "---";

  if (isExpired ) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <ActionButtonResubmit
            onClick={handleResubmitClick}
            loading={loading}
            label="Re-Initiate"
            title={resubmitTooltip}
          />
        </div>
        <SnackbarComponent />
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        {/* ADDED: Edit button placed before Cancel; uses same gating/tooltip as Cancel */}
        {isMarketAxess &&
         (<ActionButtonEditRfq
          disabled = {cancelDisabled}
          onClick={handleEditClick}
          loading={editLoading}
          label="Edit"
          title={editTooltip}
        />)}
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={cancelDisabled}
          onClick={handleCancelClick}
          sx={{ 
            textTransform: "none", 
            fontWeight: 700, 
            fontSize: '0.875rem',
            width: 'auto',
            height: 'auto',
            minHeight: 'auto',
            minWidth: BUTTON_MIN_WIDTH, 
            borderRadius: "8px",
            padding: BUTTON_PADDING,
            lineHeight: 1,
          }}
          title={cancelTooltip}
        >
          {loading ? <CircularProgress size={16} /> : "Cancel"}
        </Button>
      </div>

      <RfqCancellationConfirmPopup
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmCancel}
        singleCancelDisabled={isBlockedStatus}
        // Supplying this is what grows the third button. Undefined on a single-bond RFQ, so
        // the dialog renders exactly as it always did - No and Yes, nothing else.
        onConfirmList={isListRfq ? handleConfirmCancelList : undefined}
        // The bonds of the list, loaded by handleCancelClick from GET /order/list/{id}/items.
        // They are what the Cancel List button acts on, and the dialog also reads them to
        // decide whether that button has anything left to do.
        items={isListRfq ? listItems : []}
        itemsLoading={isListRfq ? listItemsLoading : false}
        orderId={orderId}
        accountId={accountId}
        details={{
           // show CUSIP, account and username for context
          cusip: rowData?.cusip,
          account: rowData?.account ?? rowData?.accountId ?? null,
          userName: rowData?.userName ?? null,
          protocol: rowData?.protocol,
          rfqStatus: rowData?.rfqStatus,
          // Which list this bond was raised in. Formatted here rather than passed raw because
          // the dialog's detail grid prints values as-is.
          //
          // Left out entirely when there is no number - a single-bond RFQ, or a list raised
          // before list numbers existed - so the grid simply has no List ID box rather than a
          // box reading "---". The dialog already drops null and undefined entries.
          //
          // The list's name is shown instead of its number; the number only when it has none.
          ...(rowData?.listName
            ? { listName: rowData.listName }
            : {
                listId:
                  rowData?.listId === null || rowData?.listId === undefined
                    ? undefined
                    : formatListId(rowData.listId),
              }),
          // include both naming variations for inquiry type
          rfqInquiryType: rowData?.inquiryType || rowData?.rfqInquiryType,
          // additional RFQ fields
          quoteSubmissionDeadline: rowData?.quoteSubmissionDeadline,
          rfqExpiryTime: rowData?.rfqExpiryTime,
          priceType: rowData?.priceType,
          stipulationType: rowData?.stipulationType,
          allowPartialExecution: rowData?.allowPartialExecution,
          ttl: rowData?.ttl, // show as "Quote Firm Deadline (min)" (may be null)
          // MarketAxess specific: ensure these are shown
          mktxTargetLevel: rowData?.mktxTargetLevel,
          mktxPricingProcess: rowData?.mktxPricingProcess,
          mktxDealerListMode: rowData?.mktxDealerListMode,
        }}
      />

      {/* Mount snackbar for this cell */}
      <SnackbarComponent />
    </>
  );
});

// Separate component for Source column cell that can use React hooks
const SourceColumnCell = memo(({ rowData, handleViewJson }) => {
  const setBondCharacteristicData = useBondCharacteristicStore.getState().setBondCharacteristicData;
  const setBondLadderData = useBondLadderStore.getState().setBondLadderData;

  const fetchData = useCallback(async (id, fetchFn, storeSetterFn, viewMode) => {
    try {
      if (setGlobalLoading) setGlobalLoading(true);
      
      const token = localStorage.getItem("token");
      const data = await fetchFn(token, id);
      if (data) {
        storeSetterFn(data);
        // Mark this flow so the read-only popup renders characteristic filters
        // as chips (instead of the checkbox-wise fields).
        handleViewJson({...data, viewMode, fromSourceColumn: true});
      }
    } catch (error) {
      console.error(`Error fetching ${viewMode} data:`, error);
      alert(`Failed to load ${viewMode} data`);
    } finally {
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [handleViewJson]);

  if (rowData.nameOfTheLadder && rowData.bondLadderId) {
    return (
      <ActionButton 
        onClick={() => fetchData(
          rowData.bondLadderId, 
          getBondLadderById, 
          setBondLadderData,
          "ladder"
        )} 
        label={rowData.source}
        title={rowData.source}
      />
    );
  } 
  
  if (rowData.characteristicId) {
    return (
      <ActionButton 
        onClick={() => fetchData(
          rowData.characteristicId,
          getBondCharacteristicById,
          setBondCharacteristicData,
          "characteristic"
        )} 
        label={rowData.source}
        title={rowData.source}
      />
    );
  }
  
  return rowData.source || "---";
});

// Ladder name on a bond characteristic row. Clicking it loads the ladder and
// opens the same read-only popup SourceColumnCell uses on the order blotter.
const LadderNameColumnCell = memo(({ rowData, handleViewJson }) => {
  const [loading, setLoading] = useState(false);
  const ladderId = rowData.bondLadderId;
  const ladderName = rowData.ladderName;

  const handleClick = useCallback(async () => {
    if (!ladderId) return;
    try {
      setLoading(true);
      if (setGlobalLoading) setGlobalLoading(true);

      const token = localStorage.getItem("token");
      const data = await getBondLadderById(token, ladderId);
      if (data) {
        useBondLadderStore.getState().setBondLadderData(data);
        // viewMode tells handleViewJson to render the ladder form even though
        // we're on the characteristic tab.
        handleViewJson({ ...data, viewMode: "ladder", fromSourceColumn: true });
      }
    } catch (error) {
      console.error("Error fetching bond ladder details:", error);
      alert("Failed to load bond ladder data");
    } finally {
      setLoading(false);
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [ladderId, handleViewJson]);

  if (!ladderName || !ladderId) {
    return "---";
  }

  return (
    <ActionButton
      onClick={handleClick}
      loading={loading}
      label={ladderName}
      title={`View ladder: ${ladderName}`}
    />
  );
});

// Component to render characteristics as chips in read-only mode
const CharacteristicsColumnCell = memo(({ rowData, handleViewJson }) => {
  // Memoize expensive operations
  const processedFilters = useMemo(() => {
    const rawData = parseCharacteristics(rowData.characteristics);
    return preprocessFilters(rawData);
  }, [rowData.characteristics]);
  
  const handleViewDetails = useCallback(() => {
    handleViewJson(rowData);
  }, [rowData, handleViewJson]);

  // If no characteristics data exists, show placeholder text
  if (!processedFilters || Object.keys(processedFilters).length === 0) {
    return "---"; 
  }

  // Otherwise, show chips interface with the processed filters
  return (
    <FilterChips 
      input={processedFilters}
      onViewDetails={handleViewDetails}
      maxVisibleChips={2}
      readOnly={true}
      compact={true} // Add compact prop to make chips smaller
    />
  );
});

// Ladder characteristics cell with slightly different behavior
const LadderCharacteristicsCell = memo(({ rowData, handleViewJson }) => {
  const setLadderData = useBondLadderRungFlowStore.getState().setLadderData;
  
  // Process ladder characteristics in the same way as regular characteristics
  const processedFilters = useMemo(() => {
    const rawData = parseCharacteristics(rowData.characteristics);
    return preprocessFilters(rawData);
  }, [rowData.characteristics]);
  
  const handleClick = useCallback(() => {
    handleViewJson(rowData);
    setLadderData(rowData);
  }, [rowData, handleViewJson, setLadderData]);

  // If no characteristics data exists, show placeholder text
  if (!processedFilters || Object.keys(processedFilters).length === 0) {
    return "---";
  }

  // Show chips interface with the processed filters, similar to CharacteristicsColumnCell
  return (
    <FilterChips 
      input={processedFilters}
      onViewDetails={handleClick}
      maxVisibleChips={2}
      readOnly={true}
      compact={true} // Add compact prop to make chips smaller
    />
  );
});

/**
 * The List ID cell - LST-000001, and a way into the list it names.
 *
 * <b>In plain terms:</b> click the list number on any bond of a list and the whole list opens,
 * filled in and read-only, so a trader can see what was raised together without piecing it back
 * together from the blotter.
 *
 * <b>Derived from LadderNameColumnCell</b> just above: same shape of work in the same order -
 * fetch the thing by id, write it into its own store, then hand handleViewJson a row carrying a
 * viewMode so the popup knows which form to render. Same ActionButton, same loading state, same
 * "---" when there is nothing to open.
 *
 * <b>Differs, and why it has to:</b> a ladder is fetched by its own id and lands in one store
 * object. A list has no endpoint of its own - it is addressed through any one of its bonds,
 * GET /order/list/{orderId}/items - and it fills two things: the enquiry settings and one grid
 * row per bond. Those two mappings are listRfqPrefill's, reused rather than rewritten, so the
 * list a trader READS is assembled by exactly the code that fills the list they RE-INITIATE.
 *
 * Where each value comes from follows what the endpoint can answer. The per-bond settings come
 * from the items, which now carry them; the enquiry-level settings - account, inquiry type,
 * deadline, quote firm time, settlement date - are not on that endpoint and are read off the
 * clicked row, which is exact because the backend refuses a list whose bonds disagree on them.
 */
// Opens the read-only view of the list a row belongs to. Shared by the List ID and List Name
// cells, so both open exactly the same thing.
const useOpenListView = (rowData, handleViewJson) => {
  const [loading, setLoading] = useState(false);
  const orderId = rowData?.orderId;

  const handleClick = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      if (setGlobalLoading) setGlobalLoading(true);

      const token = localStorage.getItem("token");
      // Returns [] rather than throwing on any failure, so a broken call opens an empty grid
      // instead of a dead button with no explanation.
      const items = await fetchListRfqItems(token, orderId);
      const listItems = rowData?.protocol === PROTOCOL_TYPES.MARKETAXESS
        ? await Promise.all(items.map(async (item) => {
            if (!item.rfqId) return item;
            const params = await fetchRfqStrategyParams(token, item.rfqId);
            return {
              ...item,
              strategyParameters: Object.fromEntries((params || [])
                .filter((param) => param.paramName && param.paramValue != null)
                .map((param) => {
                  const value = String(param.paramValue).trim();
                  const upper = value.toUpperCase();
                  return [param.paramName, upper === "YES" || upper === "Y" ? "1"
                    : upper === "NO" || upper === "N" ? "0" : value];
                })),
            };
          }))
        : items;

      useListRfqStore.getState().applyListPrefill({
        // View keeps the saved name and all MA bond settings/dealer-scope inference.
        sharedFields: buildViewSharedFields(rowData, listItems),
        rows: buildPrefillRows(listItems, rowData),
      });

      handleViewJson({ ...rowData, viewMode: "list", fromSourceColumn: true });
    } catch (error) {
      console.error("Error loading list RFQ details:", error);
      alert("Failed to load list details");
    } finally {
      setLoading(false);
      if (setGlobalLoading) setGlobalLoading(false);
    }
  }, [orderId, rowData, handleViewJson]);

  return { loading, handleClick };
};

const ListIdColumnCell = memo(({ rowData, handleViewJson }) => {
  const listId = rowData?.listId;
  const { loading, handleClick } = useOpenListView(rowData, handleViewJson);

  // A single-bond RFQ belongs to no list, and so has nothing to open. formatListId already
  // renders this as "---"; returning it as plain text keeps it unclickable.
  if (listId === null || listId === undefined) {
    return formatListId(listId);
  }

  return (
    <ActionButton
      onClick={handleClick}
      loading={loading}
      label={formatListId(listId)}
      title={`View list ${formatListId(listId)}`}
    />
  );
});

// The trader's name for the list, opening the same read-only view as the List ID cell.
// Plain "---" (not clickable) on a single-bond RFQ or a list given no name.
const ListNameColumnCell = memo(({ rowData, handleViewJson }) => {
  const listName = rowData?.listName;
  const { loading, handleClick } = useOpenListView(rowData, handleViewJson);

  if (!listName || rowData?.listId === null || rowData?.listId === undefined) {
    return "---";
  }

  return (
    <ActionButton
      onClick={handleClick}
      loading={loading}
      label={listName}
      title={`View list ${listName}`}
    />
  );
});

// Component to render rung ID with proper formatting
const RungIdColumnCell = memo(({ rowData }) => {
  // For normal orders (not part of ladder group), show N/A
  if (!rowData.bondLadderId || !rowData.bondLadderRungId) {
    return "N/A";
  }
  
  // For ladder group orders, show "Rung X"
  return `Rung ${rowData.bondLadderRungId}`;
});

// Export the components for use in the renderer registry
export {
  ListNameColumnCell,
  ViewInventoryColumn,
  CharacteristicsColumnCell,
  LadderCharacteristicsCell,
  LadderNameColumnCell,
  SourceColumnCell,
  RungIdColumnCell,
  ListIdColumnCell,
  EnhancedRfqActionsCell,
  AccountPositionActionsCell, // NEW export
  useOpenListView,
};


export const getOrderColumns = (userRole, view, tab) => {
  const baseColumns = buildOrderColumns(userRole, view, tab);
  const isStagingBlotter = view === '0' && tab === '0';
  const columnsWithRenderers = baseColumns.map((column) => {
    if (column.key === "bondLadderRungId" && isStagingBlotter) {
      return {
        ...column,
        render: (cellData, rowData) => (
          <RungIdColumnCell rowData={rowData} />
        )
      };
    }
    // The list number is an integer on the row; formatListId is what turns it into LST-000001
    // and into "---" when there is no list. Rendered rather than shown raw so the blotter and
    // the cancel dialog cannot drift apart on how a list is named - both call the one helper.
    if (column.key === "listId") {
      return {
        ...column,
        // handleViewJson is threaded in by the table, as it is for the Ladder Name and
        // Characteristics columns. Without it the number still renders, just unclickable.
        render: (cellData, rowData, handleViewJson) => (
          <ListIdColumnCell rowData={rowData} handleViewJson={handleViewJson} />
        ),
      };
    }
    if (column.key === "listName") {
      return {
        ...column,
        render: (cellData, rowData, handleViewJson) => (
          <ListNameColumnCell rowData={rowData} handleViewJson={handleViewJson} />
        ),
      };
    }
    if (column.key === "actions" && !isStagingBlotter) {
      return {
        ...column,
        render: (cellData, rowData) => (
          <EnhancedRfqActionsCell rowData={rowData} />
        )
      };
    }
    return column;
  });
  
  // Place source column at the end (before last 3 columns)
  const sourceColumnIndex = Math.max(columnsWithRenderers.length - 3, 0);
  
  // Return with viewInventory first, then source at calculated position, then remaining columns
  return [
    // viewInventory is NOT added here anymore - it's part of baseColumns ordering
    ...columnsWithRenderers.slice(0, sourceColumnIndex),
    {
      key: "source",
      name: "Source",
      render: (cellData, rowData, handleViewJson) => (
        <SourceColumnCell rowData={rowData} handleViewJson={handleViewJson} />
      )
    },
    ...columnsWithRenderers.slice(sourceColumnIndex)
  ];
};

export const getBondCharColumns = (userRole) => [
  {
    key: "viewInventory",
    name: "View",
    render: (cellData, rowData) => (
      <ViewInventoryColumn rowData={rowData} type="characteristic" />
    ),
  },
  { key: "account", name: "Account" },
  { key: "side", name: "Side" },
  { key: "type", name: "Type" },
  {
    key: "characteristics",
    name: "Characteristics",
    render: (cellData, rowData, handleViewJson) => (
      <CharacteristicsColumnCell rowData={rowData} handleViewJson={handleViewJson} />
    )
  },
  ...(buildBondCharColumns(userRole)
    .filter(col => col.key !== "side" && col.key !== "type" && col.key !== "account")
    .map(col => {
      if (col.key === "ladderName") {
        return {
          ...col,
          render: (cellData, rowData, handleViewJson) => (
            <LadderNameColumnCell rowData={rowData} handleViewJson={handleViewJson} />
          ),
        };
      }
      return col;
    })),
];

export const getBondLadderColumns = (userRole) => [
  {
    key: "viewInventory",
    name: "View",
    render: (cellData, rowData) => (
      <ViewInventoryColumn rowData={rowData} type="ladder" />
    ),
  },
  { key: "account", name: "Account" },
  { key: "side", name: "Side" },
  { key: "type", name: "Type" },
  {
    key: "characteristics",
    name: "Characteristics",
    render: (cellData, rowData, handleViewJson) => (
      <LadderCharacteristicsCell rowData={rowData} handleViewJson={handleViewJson} />
    ),
  },
  ...(buildBondLadderColumns(userRole).filter(col => col.key !== "side" && col.key !== "type" && col.key !== "account")),
];

export { LoadingOverlay };
